import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

// ════════════════════════════════════════════════════════════════════
// Button — source unique de vérité pour TOUS les CTAs du site.
//
// Règle d'usage (fond clair) :
//   • primary  → CTA neutre dominant (h1, formulaire principal)            [noir]
//   • brand    → CTA produit / conversion (outils, leads)                  [vert émeraude]
//   • accent   → CTA pôle phare V3 (Mandataire CEE / Artisans)             [vert lime]
//   • secondary→ CTA secondaire à côté d'un primary/brand                  [outline blanc]
//   • ghost    → action tertiaire (menus, contextes denses)                [transparent]
//   • link     → lien stylisé (inline dans une phrase)
//   • destructive → suppression / action irréversible
//
// Règle d'usage (fond sombre — section ink, mesh-dark, hero immersif) :
//   • brand / accent → conservent leur couleur (contraste OK)
//   • dark-outline → équivalent secondary sur fond sombre                  [border blanc 15%]
//   • dark-ghost   → équivalent ghost sur fond sombre                      [hover blanc 10%]
//
// `default` est conservé comme alias deprecated de `primary` pour compat.
// ════════════════════════════════════════════════════════════════════

const buttonVariants = cva(
  'group/btn relative inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-medium rounded-lg transition-all duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 focus-visible:ring-offset-2 focus-visible:ring-offset-paper disabled:pointer-events-none disabled:opacity-50 select-none',
  {
    variants: {
      variant: {
        primary:
          'bg-ink text-ivory-50 shadow-[0_1px_2px_rgba(10,10,10,.08),inset_0_1px_0_rgba(255,255,255,.08)] hover:bg-stone-800 hover:shadow-[0_2px_4px_rgba(10,10,10,.12),inset_0_1px_0_rgba(255,255,255,.10)] active:translate-y-px',
        /** @deprecated alias de `primary`. Préférer `primary`. */
        default:
          'bg-ink text-ivory-50 shadow-[0_1px_2px_rgba(10,10,10,.08),inset_0_1px_0_rgba(255,255,255,.08)] hover:bg-stone-800 hover:shadow-[0_2px_4px_rgba(10,10,10,.12),inset_0_1px_0_rgba(255,255,255,.10)] active:translate-y-px',
        brand:
          'bg-brand-600 text-white shadow-[0_1px_2px_rgba(16,185,129,.18),inset_0_1px_0_rgba(255,255,255,.18)] hover:bg-brand-700 hover:shadow-[0_2px_6px_rgba(16,185,129,.28),inset_0_1px_0_rgba(255,255,255,.20)] active:translate-y-px',
        accent:
          'bg-accent-500 text-ink shadow-[0_1px_2px_rgba(132,204,22,.18),inset_0_1px_0_rgba(255,255,255,.30)] hover:bg-accent-400 hover:shadow-[0_2px_6px_rgba(132,204,22,.28)] active:translate-y-px',
        secondary:
          'border border-stone-200 bg-paper text-ink shadow-[0_1px_2px_rgba(10,10,10,.04)] hover:bg-stone-50 hover:border-stone-300 active:translate-y-px',
        ghost:
          'text-stone-700 hover:bg-stone-100 hover:text-ink',
        outline:
          'border border-ink/15 bg-transparent text-ink hover:bg-ink hover:text-ivory-50 hover:border-ink',
        link:
          'text-brand-700 underline-offset-4 hover:underline px-0',
        destructive:
          'bg-error text-white shadow-[0_1px_2px_rgba(239,68,68,.20)] hover:bg-red-600 active:translate-y-px',
        // ──────────── Variants pour fond sombre (ink, mesh-dark, hero immersif) ────────────
        'dark-outline':
          'border border-white/15 bg-white/5 text-ivory-50 backdrop-blur-md shadow-[0_1px_2px_rgba(0,0,0,.20)] hover:bg-white/10 hover:border-white/25 active:translate-y-px',
        'dark-ghost':
          'text-ivory-50/80 hover:bg-white/8 hover:text-ivory-50',
      },
      size: {
        sm: 'h-8 px-3 text-[13px]',
        md: 'h-9 px-3.5 text-sm',
        lg: 'h-10 px-4 text-sm',
        xl: 'h-11 px-5 text-[15px]',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
